import { Input, Select, Tag, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { TableColumnInterface } from '../../../../shared/type/tableType';
import { formatDateTimePST } from '../../../../shared/utils/timeFormat';
import { kcpIngestService } from '../service/kcpIngestService';
import {
  ASSET_STATUS_COLORS,
  INGEST_STATUS_COLORS,
  IngestStatus,
  KCP_TYPE_COLORS,
  KCPContentType,
  KCPIngestItem,
} from '../type/kcpIngestType';

const KcpIngestList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '20',
    ordering: '-ingest_at',
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [_selectedList, setSelectedList] = useState<number[]>([]);

  // Filter states
  const [metaType, setMetaType] = useState<string>(
    searchParams.get('meta__source_content_type') || ''
  );
  const [status, setStatus] = useState<string>(searchParams.get('status') || '');
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');

  useEffect(() => {
    // Update searchParams when filters change
    const newParams = new URLSearchParams(searchParams);

    if (metaType) {
      newParams.set('meta__source_content_type', metaType);
    } else {
      newParams.delete('meta__source_content_type');
    }

    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status');
    }

    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }

    newParams.set('page', '1'); // Reset to first page when filters change
    setSearchParams(newParams);
  }, [metaType, status, searchQuery]);

  const columns: TableColumnInterface<KCPIngestItem>[] = [
    {
      title: 'CI ID',
      dataIndex: 'id',
      key: 'id',
      width: '6%',
      sorter: true,
      render: (_text: string, record: KCPIngestItem): JSX.Element => (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            navigate(`/kcp-ingest/detail/${record.id}`);
          }}
          style={{ color: '#1890ff', fontWeight: 500 }}
        >
          {record.id}
        </a>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'meta',
      key: 'type',
      width: '7%',
      render: (_: string, record: KCPIngestItem): JSX.Element => {
        if (!record.meta) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Tag color={KCP_TYPE_COLORS[record.meta.source_content_type] || 'default'}>
            {record.meta.source_content_type?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'KCP ID',
      dataIndex: 'kcp',
      key: 'kcp_id',
      width: '8%',
      sorter: true,
      render: (_text: string, record: KCPIngestItem): JSX.Element => {
        if (!record.kcp) return <span style={{ color: '#999' }}>-</span>;
        return <span style={{ fontWeight: 500 }}>{record.kcp.id}</span>;
      },
    },
    {
      title: 'MediaHub',
      dataIndex: 'mediahub',
      key: 'mediahub',
      width: '9%',
      render: (_: string, record: KCPIngestItem): JSX.Element => {
        if (!record.mediahub) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const linkPath =
          record.mediahub.content_type === 'season'
            ? `/season/edit2/${record.mediahub.id}`
            : `/episode/edit/${record.mediahub.id}`;
        return (
          <div>
            <Link
              to={linkPath}
              style={{ fontSize: '12px', fontWeight: 500, color: '#1890ff' }}
              onClick={e => e.stopPropagation()}
            >
              {record.mediahub.id}
            </Link>
            {record.mediahub.slug && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                {record.mediahub.slug.substring(0, 20)}
                {record.mediahub.slug.length > 20 ? '...' : ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'File Path',
      dataIndex: 'file_path',
      key: 'file_path',
      width: '15%',
      render: (_text: string, record: KCPIngestItem): JSX.Element => (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            navigate(`/kcp-ingest/detail/${record.id}`);
          }}
          style={{ color: '#1890ff', minWidth: 150, wordBreak: 'break-word' }}
        >
          {record.file_path}
        </a>
      ),
    },
    {
      title: 'Created / Modified',
      dataIndex: 'created',
      key: 'created_modified',
      width: '12%',
      render: (_text: string, record: KCPIngestItem): JSX.Element => (
        <>
          <div>{formatDateTimePST(record.created)}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {formatDateTimePST(record.modified)}
          </div>
        </>
      ),
    },
    {
      title: 'CI/Meta Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (_: string, record: KCPIngestItem): JSX.Element => {
        return (
          <>
            <Tag color={INGEST_STATUS_COLORS[record.status] || 'default'}>
              {record.status?.toUpperCase()}
            </Tag>
            <br />
            {record.meta && <span style={{ fontSize: '12px' }}>{record.meta.status}</span>}
          </>
        );
      },
    },
    {
      title: 'Video ID',
      dataIndex: 'video',
      key: 'video',
      width: '10%',
      render: (_: string, record: KCPIngestItem): JSX.Element => {
        if (!record.video) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return (
          <div>
            <a
              href={`/assets/edit/${record.video}`}
              style={{ fontSize: '12px', fontWeight: 500, color: '#1890ff' }}
            >
              {record.video}
            </a>
            <br />
            {record.video_asset_status && (
              <Tag
                color={ASSET_STATUS_COLORS[record.video_asset_status] || 'default'}
                style={{ fontSize: '10px', margin: '2px 0 0 0', padding: '0 4px' }}
              >
                {record.video_asset_status.toUpperCase()}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Ingest At',
      dataIndex: 'ingest_at',
      key: 'ingest_at',
      width: '10%',
      sorter: true,
      render: (_text: string, record: KCPIngestItem): string => {
        return formatDateTimePST(record.ingest_at);
      },
    },
  ];

  const fetchData = useCallback(
    async (params: URLSearchParams): Promise<{ data: KCPIngestItem[]; count: number }> => {
      try {
        const result = await kcpIngestService.fetch(params);
        return { data: result.results, count: result.count };
      } catch (error) {
        console.error('Failed to fetch KCP ingest list:', error);
        return { data: [], count: 0 };
      }
    },
    []
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <Component.BasePageComponent>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Tooltip title="검색 가능: File Path">
          <Input.Search
            placeholder="File Path 검색"
            allowClear
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onSearch={value => setSearchQuery(value)}
            style={{ width: 250 }}
            enterButton
          />
        </Tooltip>

        <Select
          style={{ width: 200 }}
          placeholder="Type: All"
          allowClear
          value={metaType || undefined}
          onChange={value => setMetaType(value || '')}
        >
          <Select.Option value={KCPContentType.SERIES}>Series</Select.Option>
          <Select.Option value={KCPContentType.SEASON}>Season</Select.Option>
          <Select.Option value={KCPContentType.EPISODE}>Episode</Select.Option>
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="CI Status: All"
          allowClear
          value={status || undefined}
          onChange={value => setStatus(value || '')}
        >
          <Select.Option value={IngestStatus.START}>Start</Select.Option>
          <Select.Option value={IngestStatus.PROCESSING}>Processing</Select.Option>
          <Select.Option value={IngestStatus.END}>End</Select.Option>
        </Select>
      </div>

      <Component.TableComponent<KCPIngestItem>
        key={refreshCounter}
        name="KCP Ingest List"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchData}
        columns={columns}
        options={{
          isDisableIdColumn: true,
        }}
        actions={{
          onSelectionChange: setSelectedList,
        }}
      />
    </Component.BasePageComponent>
  );
};

export default observer(KcpIngestList);
